import "bootstrap/dist/css/bootstrap.min.css";
import { Navigate, Route, Routes } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "./assets/css/backend-plugin.min.css";
import "./assets/css/backend.css";
import "./assets/vendor/@fortawesome/fontawesome-free/css/all.min.css";
import "./assets/vendor/line-awesome/dist/line-awesome/css/line-awesome.min.css";
import "./assets/vendor/remixicon/fonts/remixicon.css";

import DashboardLayout from "./components/layout/DashboardLayout";
import LoginLayout from "./components/Login/LoginLayout";

import { LanguageProvider, useTranslate } from "./hooks/LanguageProvider";
import { ThemeProvider } from "./hooks/ThemeContext";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { UserProvider } from "./context/UserContext";

import { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import Chat from "./pages/Chat";
import { Chats } from "./pages/Chats";
import Adherer from "./pages/Adherer";
import ConfirmEmail from "./pages/ConfirmEmail";
import Connection from "./pages/Connection";
import Dashboard from "./pages/Dashboard";
import DemandeOffre from "./pages/DemandeOffre";
import ForgotPasswordCover from "./pages/ForgotPasswordCover";
import RFQList from "./pages/FRQs";
import EmailDispatch from "./pages/EmailDispatch/index";
import ExpertProfile from "./pages/ExpertProfile";
import { Experts } from "./pages/Experts";
import { GroupeVehicles } from "./pages/GroupVehicule";
import Help from "./pages/Help";
import UserHistory from "./pages/history-user";
import HistoryVehicle from "./pages/history-vehicle";
import LoginForm from "./pages/Login";
import NationalCartography from "./pages/NationalCartography";
import NotFound from "./pages/NotFound";
import { Notifications } from "./pages/Notification";
import NotificationCenter from "./pages/NotificationCenter";
import {
  default as OEMProfile,
  default as OEMProfilePage,
} from "./pages/OEMProfile";
import OEMCertifications from "./pages/OEMCertifications";
import OEMProducts from "./pages/OEMProducts";
import { OEMs } from "./pages/OEMs";
import { Permission } from "./pages/Permission";
import { Preferences } from "./pages/Preference";
import Profile from "./pages/Profile";
import ProfileSupplier from "./pages/ProfileSupplier";
import Role from "./pages/Role";
import { Settings } from "./pages/Settings";
import SignUp from "./pages/SignUp";
import { default as Supplier, default as Vehicle } from "./pages/Supplier";
import SupplierCertifications from "./pages/SupplierCertifications";
import SupplierDocuments from "./pages/SupplierDocuments";
import SupplierMachines from "./pages/SupplierMachines";
import SupplierProducts from "./pages/SupplierProducts";
import SupplierRFQDetails from "./pages/SupplierRFQDetails";
import { Suppliers } from "./pages/Suppliers";
import User from "./pages/User";
import { Users } from "./pages/Users";
import NewVehicle from "./pages/Vehicle-New";
import AccessDenied from "./pages/AccessDenied";
import usePermissions from "./hooks/usePermissions";

type PermissionAction = "can_create" | "can_read" | "can_update" | "can_delete";

function ProtectedRoute({
  children,
  allowedRoles,
  permissionId,
  permissionIds,
  permissionAction = "can_read",
}: {
  children: React.ReactNode;
  allowedRoles?: number[];
  permissionId?: number;
  permissionIds?: number[];
  permissionAction?: PermissionAction;
}) {
  const { user, loading } = useAuth();
  const roleId = user ? String(user.id_role) : null;
  const {
    userPermissions,
    loading: permissionsLoading,
    error: permissionsError,
  } = usePermissions(roleId);

  if (loading) {
    return (
      <div id="loading" style={{ display: "none" }}>
        <div id="loading-center"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.id_role)) {
    return <AccessDenied />;
  }

  const requiredPermissionIds =
    permissionIds || (permissionId !== undefined ? [permissionId] : []);

  if (requiredPermissionIds.length > 0) {
    if (permissionsLoading) {
      return (
        <div id="loading" style={{ display: "none" }}>
          <div id="loading-center"></div>
        </div>
      );
    }

    const hasPermission = userPermissions.some(
      (item) =>
        requiredPermissionIds.includes(item.id_permission) &&
        item[permissionAction] === 1,
    );

    if (permissionsError || !hasPermission) {
      return <AccessDenied />;
    }
  }

  return <>{children}</>;
}

function AppContent() {
  const { lang } = useTranslate();

  useEffect(() => {
    const isArabic = lang === "ar";

    document.documentElement.setAttribute("dir", isArabic ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", isArabic ? "ar" : "en");
    document.body.classList.toggle("lang-ar", isArabic);
  }, [lang]);

  return (
    <>
      <div
        className="wrapper"
        style={{
          transition: "width 0.3s",
          backgroundColor: "#fff",
          height: "100vh",
          padding: "0px",
        }}
      >
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute permissionId={1}>
                <DashboardLayout>
                  <Dashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Suppliers"
            element={
              <ProtectedRoute permissionId={2}>
                <DashboardLayout>
                  <Suppliers />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Supplier"
            element={
              <ProtectedRoute permissionId={2}>
                <DashboardLayout>
                  <Supplier />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Supplier/add"
            element={
              <ProtectedRoute permissionId={2} permissionAction="can_create">
                <DashboardLayout>
                  <Supplier />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Supplier/edit/:id_vehicule"
            element={
              <ProtectedRoute permissionId={2} permissionAction="can_update">
                <DashboardLayout>
                  <Vehicle />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/oem-profile"
            element={
              <ProtectedRoute permissionId={9}>
                <DashboardLayout>
                  <OEMProfile />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/OEMs"
            element={
              <ProtectedRoute permissionId={13}>
                <DashboardLayout>
                  <OEMs />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Experts"
            element={
              <ProtectedRoute permissionId={4}>
                <DashboardLayout>
                  <Experts />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/rfqs"
            element={
              <ProtectedRoute permissionId={11}>
                <DashboardLayout>
                  <RFQList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* RFQ - Create */}
          <Route
            path="/rfq/add"
            element={
              <ProtectedRoute permissionId={12} permissionAction="can_create">
                <DashboardLayout>
                  <DemandeOffre />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* RFQ - Read */}
          <Route
            path="/rfq/:id"
            element={
              <ProtectedRoute permissionId={11}>
                <DashboardLayout>
                  <DemandeOffre />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* RFQ - Edit */}
          <Route
            path="/rfq/edit/:id"
            element={
              <ProtectedRoute permissionId={11} permissionAction="can_update">
                <DashboardLayout>
                  <DemandeOffre />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Ancienne route création */}
          <Route
            path="/new-rfq"
            element={
              <ProtectedRoute permissionId={12} permissionAction="can_create">
                <DashboardLayout>
                  <Navigate to="/rfq/add" replace />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/oem-profile"
            element={
              <ProtectedRoute permissionId={9}>
                <DashboardLayout>
                  <OEMProfilePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/oem-profile/:id_user"
            element={
              <ProtectedRoute permissionIds={[9, 13]}>
                <DashboardLayout>
                  <OEMProfilePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Vehicle-new/edit/:id_vehicule"
            element={
              <ProtectedRoute permissionId={2} permissionAction="can_update">
                <DashboardLayout>
                  <NewVehicle />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute permissionId={3}>
                <DashboardLayout>
                  <Users />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/email-dispatch"
            element={
              <ProtectedRoute permissionId={4}>
                <DashboardLayout>
                  <EmailDispatch />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/User"
            element={
              <ProtectedRoute permissionId={4} permissionAction="can_create">
                <DashboardLayout>
                  <User />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/User/:userurlID"
            element={
              <ProtectedRoute permissionId={4}>
                <DashboardLayout>
                  <User />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/history-vehicle"
            element={
              <ProtectedRoute permissionId={4}>
                <DashboardLayout>
                  <HistoryVehicle />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/history-user"
            element={
              <ProtectedRoute permissionId={4}>
                <DashboardLayout>
                  <UserHistory />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Help />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Connection"
            element={
              <ProtectedRoute permissionId={4}>
                <DashboardLayout>
                  <Connection />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Settings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/groupevehicles"
            element={
              <ProtectedRoute permissionId={2}>
                <DashboardLayout>
                  <GroupeVehicles />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/role"
            element={
              <ProtectedRoute permissionId={5}>
                <DashboardLayout>
                  <Role />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/role/permission/:id_user/:id_role"
            element={
              <ProtectedRoute permissionId={5} permissionAction="can_update">
                <DashboardLayout>
                  <Permission />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Profile />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/expert-profile"
            element={
              <ProtectedRoute permissionId={15}>
                <DashboardLayout>
                  <ExpertProfile />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/expert-profile/:id_user"
            element={
              <ProtectedRoute permissionIds={[15, 4]}>
                <DashboardLayout>
                  <ExpertProfile />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/national-cartography"
            element={
              <ProtectedRoute permissionId={14}>
                <DashboardLayout>
                  <NationalCartography />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profileSupplier"
            element={
              <ProtectedRoute permissionId={10}>
                <DashboardLayout>
                  <ProfileSupplier />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profileSupplier/:id_user"
            element={
              <ProtectedRoute permissionIds={[10, 2]}>
                <DashboardLayout>
                  <ProfileSupplier />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/chats"
            element={
              <ProtectedRoute permissionId={7}>
                <DashboardLayout>
                  <Chats />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute permissionId={6}>
                <DashboardLayout>
                  <Notifications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications/:id_notification"
            element={
              <ProtectedRoute permissionId={6}>
                <DashboardLayout>
                  <Notifications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/chat"
            element={
              <ProtectedRoute permissionId={7}>
                <DashboardLayout>
                  <Chat />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/chat/:id_chat/:id_received"
            element={
              <ProtectedRoute permissionId={7}>
                <DashboardLayout>
                  <Chat />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notification-center/:id_user"
            element={
              <ProtectedRoute permissionId={6}>
                <DashboardLayout>
                  <NotificationCenter />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/preference"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Preferences />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/SupplierProducts"
            element={
              <ProtectedRoute permissionId={10}>
                <DashboardLayout>
                  <SupplierProducts />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/OEMProducts"
            element={
              <ProtectedRoute permissionId={9}>
                <DashboardLayout>
                  <OEMProducts />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/OEMCertifications"
            element={
              <ProtectedRoute permissionId={9}>
                <DashboardLayout>
                  <OEMCertifications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/SupplierMachines"
            element={
              <ProtectedRoute permissionId={10}>
                <DashboardLayout>
                  <SupplierMachines />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/SupplierCertifications"
            element={
              <ProtectedRoute permissionId={10}>
                <DashboardLayout>
                  <SupplierCertifications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/SupplierDocuments"
            element={
              <ProtectedRoute permissionId={10}>
                <DashboardLayout>
                  <SupplierDocuments />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/supplier-rfq/:id"
            element={
              <ProtectedRoute permissionId={11}>
                <DashboardLayout>
                  <SupplierRFQDetails />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/login"
            element={
              <LoginLayout>
                <LoginForm />
              </LoginLayout>
            }
          />

          <Route
            path="/sign-up"
            element={
              <LoginLayout>
                <SignUp />
              </LoginLayout>
            }
          />
          <Route
            path="/adherer"
            element={
              <LoginLayout>
                <Adherer />
              </LoginLayout>
            }
          />
          <Route
            path="/new-rfq"
            element={
              <ProtectedRoute permissionId={12} permissionAction="can_create">
                <DashboardLayout>
                  <DemandeOffre />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route path="/forgot-password" element={<ForgotPasswordCover />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        rtl={lang === "ar"} // ✅ IMPORTANT
        theme="light"
        limit={1}
      />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <UserProvider>
        <ThemeProvider>
          <LanguageProvider>
            <AppContent />
          </LanguageProvider>
        </ThemeProvider>
      </UserProvider>
    </AuthProvider>
  );
}

export default App;
